import React, { FC, useEffect, useState } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import { LatLngExpression } from 'leaflet';
import { Driver } from '../../types/driver.types';
import TruckMarker from './TruckMarker';
import { DriverLocations } from '../../types/location.types';
import MapController from './MapController';
import 'leaflet/dist/leaflet.css';

interface DriverMapProps {
  drivers: Driver[];
  locations: DriverLocations;
  center: LatLngExpression;
  selectedDriver: string | null;
  onDriverClick?: (driverId: string) => void;
}

export const DriverMap: FC<DriverMapProps> = ({
  drivers,
  locations,
  center,
  selectedDriver,
  onDriverClick
}) => {
  const [mapCenter, setMapCenter] = useState<LatLngExpression>(center);

  // Debug logging
  console.log('DriverMap Debug:');
  console.log('Drivers:', drivers);
  console.log('Locations:', locations);
  console.log('Location keys:', Object.keys(locations));
  console.log('Selected driver:', selectedDriver);

  // Auto-center map when a driver is selected
  useEffect(() => {
    if (selectedDriver && locations[selectedDriver]) {
      const location = locations[selectedDriver];
      setMapCenter([location.lat, location.lng]);
    }
  }, [selectedDriver, locations]);

  // Get active drivers with locations - with detailed debugging
  const activeDriversWithLocations = drivers.filter(driver => {
    console.log(`Checking driver ${driver.name}:`);
    console.log(`  - driver.id: ${driver.id}`);
    console.log(`  - driver.uid: ${driver.uid}`);
    console.log(`  - driver.active: ${driver.active}`);
    
    // Try both uid and id to find location
    const locationByUid = locations[driver.uid];
    const locationById = locations[driver.id];
    
    console.log(`  - location by uid (${driver.uid}):`, locationByUid);
    console.log(`  - location by id (${driver.id}):`, locationById);
    
    const location = locationByUid || locationById;
    
    if (location) {
      console.log(`  - location found, status: ${location.status}`);
    } else {
      console.log(`  - no location found`);
    }
    
    return driver.active && location && location.status === 'active';
  });

  console.log('Active drivers with locations:', activeDriversWithLocations);

  return (
    <div className="h-[600px] w-full relative">
      {/* Status indicator */}
      <div className="absolute top-4 right-4 z-[1000] bg-white px-3 py-2 rounded-lg shadow-md">
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${activeDriversWithLocations.length > 0 ? 'bg-green-500' : 'bg-gray-400'}`}></div>
          <span className="text-sm font-medium">
            {activeDriversWithLocations.length} Active Driver{activeDriversWithLocations.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Debug info */}
      <div className="absolute top-16 right-4 z-[1000] bg-white px-3 py-2 rounded-lg shadow-md text-xs">
        <div>Total drivers: {drivers.length}</div>
        <div>Total locations: {Object.keys(locations).length}</div>
        <div>Active with locations: {activeDriversWithLocations.length}</div>
      </div>

      <MapContainer 
        center={center} 
        zoom={13} 
        className="h-full w-full rounded-lg"
        scrollWheelZoom={true}
      >
        <MapController center={mapCenter} />
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        {activeDriversWithLocations.map(driver => {
          // Try both uid and id to get location
          const location = locations[driver.uid] || locations[driver.id];
          
          if (!location) {
            console.warn(`No location found for driver ${driver.name} (uid: ${driver.uid}, id: ${driver.id})`);
            return null;
          }
          
          return (
            <TruckMarker
              key={driver.id}
              driver={driver}
              location={location}
              isSelected={selectedDriver === driver.id}
              onClick={() => onDriverClick?.(driver.id)}
            />
          );
        })}
      </MapContainer>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 z-[1000] bg-white p-3 rounded-lg shadow-md">
        <h4 className="text-sm font-semibold mb-2">Legend</h4>
        <div className="space-y-1 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span>Active Driver</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span>Selected Driver</span>
          </div>
        </div>
      </div>
    </div>
  );
};